import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useParams } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { ListResponse, Location } from "../../api/types";
import { useWorkspaceLens, WorkspacePointer } from "../../app/workspaceLens";
import {
  formatDateTime,
  formatMoney,
  titleCase,
  zonedDateTimeInput,
  zonedInputToISO,
} from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EditorPage,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { EditorPageSkeleton, SkeletonTable } from "../../ui/skeletons";
import type { AvailabilityRule, RateRule } from "./policyTypes";
import { useVenues } from "./queries";

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function dayLabel(days: number[]): string {
  return days.length ? days.map((day) => weekdays[day]).join(", ") : "Every day";
}

function availabilityPayload(item: AvailabilityRule, published = item.published) {
  return {
    name: item.name,
    kind: item.kind,
    days: item.days,
    localStartTime: item.localStartTime,
    localEndTime: item.localEndTime,
    startsAt: item.startsAt ?? null,
    endsAt: item.endsAt ?? null,
    minimumLeadMinutes: item.minimumLeadMinutes,
    maximumAdvanceMinutes: item.maximumAdvanceMinutes ?? null,
    blocking: item.blocking,
    published,
    version: item.version,
  };
}

function ratePayload(item: RateRule, active = item.active) {
  return {
    name: item.name,
    priority: item.priority,
    customerType: item.customerType,
    days: item.days,
    localStartTime: item.localStartTime,
    localEndTime: item.localEndTime,
    validFrom: item.validFrom,
    validUntil: item.validUntil,
    amountMinor: item.amountMinor,
    unit: item.unit,
    taxInclusive: item.taxInclusive,
    active,
    version: item.version,
  };
}

export function LocationPolicyEditorPage() {
  const { venueId = "", locationId = "" } = useParams<{ venueId: string; locationId: string }>();
  const lens = useWorkspaceLens();
  // Pricing is lettings work. On the events side this page is about when a room can be used at
  // all, and the rate rules that only matter once the room is let out are neither loaded nor shown.
  const pricing = lens.isBookings;
  const venues = useVenues();
  const venue = venues.data?.items.find((item) => item.id === venueId);
  const locations = useQuery({
    queryKey: ["venues", venueId, "locations"],
    queryFn: () => api<ListResponse<Location>>(`/api/v1/venues/${venueId}/locations`),
    enabled: Boolean(venue),
  });
  const location = locations.data?.items.find((item) => item.id === locationId);
  const [availabilityForm, setAvailabilityForm] = useState<AvailabilityRule | "new" | null>(null);
  const [rateForm, setRateForm] = useState<RateRule | "new" | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const base = venue && location ? `/api/v1/venues/${venue.id}/locations/${location.id}` : "";
  const backTo = `/venues?venue=${encodeURIComponent(venueId)}`;
  const availability = useQuery({
    queryKey: ["venues", venueId, "locations", locationId, "availability-rules"],
    queryFn: () => api<ListResponse<AvailabilityRule>>(`${base}/availability-rules`),
    enabled: Boolean(base),
  });
  const rates = useQuery({
    queryKey: ["venues", venueId, "locations", locationId, "rate-rules"],
    queryFn: () => api<ListResponse<RateRule>>(`${base}/rate-rules`),
    enabled: Boolean(base) && pricing,
  });

  async function saveAvailability(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!venue || !location || !availabilityForm) return;
    const form = new FormData(event.currentTarget);
    const startsAt = String(form.get("startsAt") ?? "");
    const endsAt = String(form.get("endsAt") ?? "");
    const maximumAdvance = String(form.get("maximumAdvanceMinutes") ?? "");
    const existing = availabilityForm === "new" ? null : availabilityForm;
    const payload = {
      name: String(form.get("name")),
      kind: String(form.get("kind")),
      days: form.getAll("days").map(Number),
      localStartTime: String(form.get("localStartTime")),
      localEndTime: String(form.get("localEndTime")),
      startsAt: startsAt ? zonedInputToISO(startsAt, venue.timezone) : null,
      endsAt: endsAt ? zonedInputToISO(endsAt, venue.timezone) : null,
      minimumLeadMinutes: Number(form.get("minimumLeadMinutes")),
      maximumAdvanceMinutes: maximumAdvance ? Number(maximumAdvance) : null,
      blocking: form.get("blocking") === "on",
      published: form.get("published") === "on",
      version: existing?.version ?? 0,
    };
    setWorking(true);
    setError(undefined);
    try {
      await api(
        existing ? `${base}/availability-rules/${existing.id}` : `${base}/availability-rules`,
        { method: existing ? "PUT" : "POST", body: jsonBody(payload) },
      );
      await availability.refetch();
      setAvailabilityForm(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function saveRate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!rateForm) return;
    const form = new FormData(event.currentTarget);
    const existing = rateForm === "new" ? null : rateForm;
    const payload = {
      name: String(form.get("name")),
      priority: Number(form.get("priority")),
      customerType: String(form.get("customerType")),
      days: form.getAll("days").map(Number),
      localStartTime: String(form.get("localStartTime")),
      localEndTime: String(form.get("localEndTime")),
      validFrom: String(form.get("validFrom")),
      validUntil: String(form.get("validUntil")),
      amountMinor: Math.round(Number(form.get("amount")) * 100),
      unit: String(form.get("unit")),
      taxInclusive: form.get("taxInclusive") === "on",
      active: form.get("active") === "on",
      version: existing?.version ?? 0,
    };
    setWorking(true);
    setError(undefined);
    try {
      await api(existing ? `${base}/rate-rules/${existing.id}` : `${base}/rate-rules`, {
        method: existing ? "PUT" : "POST",
        body: jsonBody(payload),
      });
      await rates.refetch();
      setRateForm(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function toggleAvailability(item: AvailabilityRule) {
    setWorking(true);
    setError(undefined);
    try {
      await api(`${base}/availability-rules/${item.id}`, {
        method: "PUT",
        body: jsonBody(availabilityPayload(item, !item.published)),
      });
      await availability.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function toggleRate(item: RateRule) {
    setWorking(true);
    setError(undefined);
    try {
      await api(`${base}/rate-rules/${item.id}`, {
        method: "PUT",
        body: jsonBody(ratePayload(item, !item.active)),
      });
      await rates.refetch();
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (venues.isLoading || (venue && locations.isLoading)) return <EditorPageSkeleton />;
  if (venues.error || locations.error)
    return <ErrorPanel error={venues.error ?? locations.error} />;

  const availabilityItems = availability.data?.items ?? [];
  const rateItems = rates.data?.items ?? [];
  const availabilityValue = availabilityForm === "new" ? null : availabilityForm;
  const rateValue = rateForm === "new" ? null : rateForm;
  const title =
    availabilityForm !== null
      ? availabilityValue
        ? "Edit availability rule"
        : "Add availability rule"
      : rateForm !== null
        ? rateValue
          ? "Edit pricing rule"
          : "Add pricing rule"
        : `${location?.name ?? "Space"} ${pricing ? "policies" : "hours and closures"}`;

  return (
    <EditorPage
      title={title}
      description={venue ? `All times use ${venue.timezone}.` : "Location policy editor"}
      backTo={backTo}
      backLabel="Back to venue"
    >
      {!venue || !location ? (
        <EmptyState
          title="Location not found"
          description="The venue or location is outside your current access scope or no longer exists."
        />
      ) : availabilityForm !== null ? (
        <Card>
          {error ? <ErrorPanel error={error} /> : null}
          <form
            className="form-grid"
            onSubmit={saveAvailability}
            key={availabilityValue?.id ?? "new-availability"}
          >
            <Field label="Name">
              <input
                name="name"
                required
                defaultValue={availabilityValue?.name ?? "Regular opening hours"}
              />
            </Field>
            <Field label="Rule type">
              <select name="kind" defaultValue={availabilityValue?.kind ?? "opening_hours"}>
                {[
                  "opening_hours",
                  "private_block",
                  "maintenance",
                  "holiday",
                  "emergency",
                  "lead_time",
                ].map((kind) => (
                  <option value={kind} key={kind}>
                    {titleCase(kind)}
                  </option>
                ))}
              </select>
            </Field>
            <fieldset className="full">
              <legend>Weekdays</legend>
              <div className="inline">
                {weekdays.map((day, index) => (
                  <label className="check-field" key={day}>
                    <input
                      name="days"
                      type="checkbox"
                      value={index}
                      defaultChecked={
                        availabilityValue
                          ? availabilityValue.days.includes(index)
                          : index >= 1 && index <= 5
                      }
                    />{" "}
                    {day}
                  </label>
                ))}
              </div>
            </fieldset>
            <Field label="Local start time">
              <input
                name="localStartTime"
                type="time"
                defaultValue={availabilityValue?.localStartTime ?? "09:00"}
              />
            </Field>
            <Field label="Local end time">
              <input
                name="localEndTime"
                type="time"
                defaultValue={availabilityValue?.localEndTime ?? "17:00"}
              />
            </Field>
            <Field label="Absolute start" hint="Optional for one-off closures.">
              <input
                name="startsAt"
                type="datetime-local"
                defaultValue={zonedDateTimeInput(availabilityValue?.startsAt, venue.timezone)}
              />
            </Field>
            <Field label="Absolute end">
              <input
                name="endsAt"
                type="datetime-local"
                defaultValue={zonedDateTimeInput(availabilityValue?.endsAt, venue.timezone)}
              />
            </Field>
            <Field label="Minimum lead (minutes)">
              <input
                name="minimumLeadMinutes"
                type="number"
                min={0}
                defaultValue={availabilityValue?.minimumLeadMinutes ?? 0}
              />
            </Field>
            <Field label="Maximum advance (minutes)">
              <input
                name="maximumAdvanceMinutes"
                type="number"
                min={1}
                defaultValue={availabilityValue?.maximumAdvanceMinutes}
              />
            </Field>
            <label className="check-field">
              <input name="blocking" type="checkbox" defaultChecked={availabilityValue?.blocking} />{" "}
              Blocking rule
            </label>
            <label className="check-field">
              <input
                name="published"
                type="checkbox"
                defaultChecked={availabilityValue?.published ?? true}
              />{" "}
              Publish immediately
            </label>
            <div className="full">
              <FormActions
                submitting={working}
                submitLabel="Save rule"
                onCancel={() => setAvailabilityForm(null)}
              />
            </div>
          </form>
        </Card>
      ) : rateForm !== null ? (
        <Card>
          {error ? <ErrorPanel error={error} /> : null}
          <form className="form-grid" onSubmit={saveRate} key={rateValue?.id ?? "new-rate"}>
            <Field label="Name">
              <input name="name" required defaultValue={rateValue?.name ?? "Standard rate"} />
            </Field>
            <Field label="Priority" hint="Lower numbers are evaluated first.">
              <input
                name="priority"
                type="number"
                min={0}
                max={10000}
                required
                defaultValue={rateValue?.priority ?? 100}
              />
            </Field>
            <Field label="Customer type">
              <select name="customerType" defaultValue={rateValue?.customerType ?? ""}>
                <option value="">All customers</option>
                <option value="individual">Individual</option>
                <option value="organisation">Organization</option>
                <option value="government">Government</option>
                <option value="nonprofit">Nonprofit</option>
              </select>
            </Field>
            <Field label="Unit">
              <select name="unit" defaultValue={rateValue?.unit ?? "hour"}>
                <option value="hour">Per hour</option>
                <option value="day">Per day</option>
                <option value="booking">Per booking</option>
                <option value="person">Per person</option>
              </select>
            </Field>
            <fieldset className="full">
              <legend>Weekdays</legend>
              <div className="inline">
                {weekdays.map((day, index) => (
                  <label className="check-field" key={day}>
                    <input
                      name="days"
                      type="checkbox"
                      value={index}
                      defaultChecked={rateValue?.days.includes(index)}
                    />{" "}
                    {day}
                  </label>
                ))}
              </div>
            </fieldset>
            <Field label="Local start time">
              <input name="localStartTime" type="time" defaultValue={rateValue?.localStartTime} />
            </Field>
            <Field label="Local end time">
              <input name="localEndTime" type="time" defaultValue={rateValue?.localEndTime} />
            </Field>
            <Field label="Valid from">
              <input name="validFrom" type="date" defaultValue={rateValue?.validFrom} />
            </Field>
            <Field label="Valid until">
              <input name="validUntil" type="date" defaultValue={rateValue?.validUntil} />
            </Field>
            <Field label="Amount (USD)">
              <input
                name="amount"
                type="number"
                min={0}
                step={0.01}
                required
                defaultValue={rateValue ? (rateValue.amountMinor / 100).toFixed(2) : ""}
              />
            </Field>
            <label className="check-field">
              <input name="taxInclusive" type="checkbox" defaultChecked={rateValue?.taxInclusive} />{" "}
              Tax included
            </label>
            <label className="check-field">
              <input name="active" type="checkbox" defaultChecked={rateValue?.active ?? true} />{" "}
              Active
            </label>
            <div className="full">
              <FormActions
                submitting={working}
                submitLabel="Save rate"
                onCancel={() => setRateForm(null)}
              />
            </div>
          </form>
        </Card>
      ) : (
        <div className="stack">
          {error ? <ErrorPanel error={error} /> : null}
          {availability.isLoading || (pricing && rates.isLoading) ? (
            <>
              <SkeletonTable columns={4} rows={2} />
              {pricing ? <SkeletonTable columns={5} rows={2} /> : null}
            </>
          ) : null}
          {availability.error || (pricing && rates.error) ? (
            <ErrorPanel error={availability.error ?? rates.error} />
          ) : null}
          {pricing ? (
            <Notice tone="info">
              At least one published opening-hours rule is required before an online offering can be
              published. Blocking rules and live allocations always take priority.
            </Notice>
          ) : null}
          <Card>
            <CardHeader
              title="Availability"
              description={
                pricing
                  ? "Opening hours, closures, maintenance, holidays, emergencies, and lead-time policy."
                  : "Opening hours, closures, maintenance, holidays, and emergencies: when this space can be used at all."
              }
              actions={
                <button
                  className="button primary small"
                  type="button"
                  onClick={() => setAvailabilityForm("new")}
                >
                  <Plus size={15} /> Add rule
                </button>
              }
            />
            {availabilityItems.length ? (
              <DataTable
                caption="Availability rules"
                items={availabilityItems}
                getKey={(item) => item.id}
                columns={[
                  {
                    label: "Rule",
                    render: (item) => (
                      <>
                        <strong>{item.name}</strong>
                        <br />
                        <small>{titleCase(item.kind)}</small>
                      </>
                    ),
                  },
                  {
                    label: "Schedule",
                    render: (item) =>
                      item.startsAt || item.endsAt
                        ? `${formatDateTime(item.startsAt, venue.timezone)} – ${formatDateTime(item.endsAt, venue.timezone)}`
                        : `${dayLabel(item.days)}${item.localStartTime ? ` · ${item.localStartTime}–${item.localEndTime}` : ""}`,
                  },
                  {
                    label: "State",
                    render: (item) => <StatusPill value={item.published ? "published" : "draft"} />,
                  },
                  {
                    label: "Actions",
                    render: (item) => (
                      <div className="inline">
                        <button
                          className="button ghost small"
                          type="button"
                          onClick={() => setAvailabilityForm(item)}
                        >
                          Edit
                        </button>
                        <button
                          className="button secondary small"
                          type="button"
                          disabled={working}
                          onClick={() => toggleAvailability(item)}
                        >
                          {item.published ? "Unpublish" : "Publish"}
                        </button>
                      </div>
                    ),
                  },
                ]}
              />
            ) : (
              <EmptyState
                title="No availability rules"
                description="Add opening hours before enabling customer self-service."
              />
            )}
          </Card>
          {pricing ? (
            <Card>
              <CardHeader
                title="Pricing rules"
                description="Priority-ordered rates by audience, weekday, time window, and validity period."
                actions={
                  <button
                    className="button primary small"
                    type="button"
                    onClick={() => setRateForm("new")}
                  >
                    <Plus size={15} /> Add rate
                  </button>
                }
              />
              {rateItems.length ? (
                <DataTable
                  caption="Rate rules"
                  items={rateItems}
                  getKey={(item) => item.id}
                  columns={[
                    {
                      label: "Rate",
                      render: (item) => (
                        <>
                          <strong>{item.name}</strong>
                          <br />
                          <small>
                            Priority {item.priority} ·{" "}
                            {item.customerType ? titleCase(item.customerType) : "All customers"}
                          </small>
                        </>
                      ),
                    },
                    {
                      label: "Schedule",
                      render: (item) =>
                        `${dayLabel(item.days)}${item.localStartTime ? ` · ${item.localStartTime}–${item.localEndTime}` : ""}`,
                    },
                    {
                      label: "Price",
                      render: (item) =>
                        `${formatMoney(item.amountMinor, item.currency)} / ${item.unit}`,
                    },
                    {
                      label: "State",
                      render: (item) => <StatusPill value={item.active ? "active" : "inactive"} />,
                    },
                    {
                      label: "Actions",
                      render: (item) => (
                        <div className="inline">
                          <button
                            className="button ghost small"
                            type="button"
                            onClick={() => setRateForm(item)}
                          >
                            Edit
                          </button>
                          <button
                            className="button secondary small"
                            type="button"
                            disabled={working}
                            onClick={() => toggleRate(item)}
                          >
                            {item.active ? "Disable" : "Enable"}
                          </button>
                        </div>
                      ),
                    },
                  ]}
                />
              ) : (
                <EmptyState
                  title="No pricing rules"
                  description="Add a rate before requiring payment for public bookings."
                />
              )}
            </Card>
          ) : (
            <WorkspacePointer target="bookings">
              Prices for this space are set in the Booking workspace.
            </WorkspacePointer>
          )}
        </div>
      )}
    </EditorPage>
  );
}
